import { CartItemTypes } from '../enums/CartItemTypes';
import { DiscountCodeScope } from '../enums/DiscountCodeScopes';
import { DiscountType } from '../enums/DiscountTypes';
import { ICartDiscount } from '../interfaces/ICartDiscount';
import { ICartItem } from '../interfaces/ICartItem';

function roundToCents(value: number): number {
  const sign = value < 0 ? -1 : 1;
  return sign * Math.round(Math.abs(value) * 100) / 100;
}

function lineKey(item: ICartItem): string {
  return `${item.itemId}:${item.position}`;
}

function sumKioskConsumed(cartItems: ICartItem[]): number {
  return cartItems
    .filter(item => item.itemType === CartItemTypes.Product || item.itemType === CartItemTypes.Package)
    .reduce(
      (sum, item) =>
        sum +
        (item.discountCodeUnitReduction ?? 0) * item.quantity +
        (item.discountCodeLineRemainder ?? 0),
      0
    );
}

function allocateByLine(lineTotals: number[], target: number): number[] {
  if (lineTotals.length === 0 || target <= 0) {
    return lineTotals.map(() => 0);
  }

  const baseTotal = lineTotals.reduce((sum, value) => sum + value, 0);
  if (baseTotal <= 0) {
    return lineTotals.map(() => 0);
  }

  const cappedTarget = Math.min(roundToCents(target), roundToCents(baseTotal));
  let remaining = cappedTarget;
  const allocations = lineTotals.map(() => 0);

  for (let i = 0; i < lineTotals.length; i++) {
    const proportional = i === lineTotals.length - 1
      ? Math.min(remaining, lineTotals[i])
      : Math.min(
        roundToCents(lineTotals[i] / baseTotal * cappedTarget),
        remaining,
        lineTotals[i]
      );

    allocations[i] = proportional;
    remaining -= proportional;
  }

  for (let i = 0; i < allocations.length && remaining > 0; i++) {
    const room = lineTotals[i] - allocations[i];
    if (room <= 0) {
      continue;
    }

    const extra = Math.min(room, remaining);
    allocations[i] += extra;
    remaining -= extra;
  }

  return allocations;
}

function computeTicketLineReductions(
  discount: ICartDiscount,
  cartItems: ICartItem[]
): Map<string, number> {
  const tickets = cartItems.filter(item => item.itemType === CartItemTypes.Ticket);
  const result = new Map<string, number>();

  if (
    discount.type !== DiscountType.DiscountCode ||
    (discount.scope !== DiscountCodeScope.Tickets && discount.scope !== DiscountCodeScope.Both) ||
    tickets.length === 0
  ) {
    return result;
  }

  const lineTotals = tickets.map(item => roundToCents(item.totalPrice));

  if (discount.isPercent) {
    const fraction = discount.discountFraction ?? 0;
    tickets.forEach((item, index) => {
      const full = lineTotals[index];
      const reduced = roundToCents(full * (1 - fraction));
      result.set(lineKey(item), roundToCents(full - reduced));
    });
    return result;
  }

  const ticketBase = roundToCents(lineTotals.reduce((sum, value) => sum + value, 0));
  const remainingBudget = Math.max(0, (discount.value ?? 0) - sumKioskConsumed(cartItems));
  const allocations = allocateByLine(lineTotals, Math.min(remainingBudget, ticketBase));
  tickets.forEach((item, index) => result.set(lineKey(item), allocations[index] ?? 0));
  return result;
}

export function getCartItemDisplayTotal(
  item: ICartItem,
  discount: ICartDiscount | null | undefined,
  cartItems: ICartItem[] = []
): number {
  if (
    !discount ||
    discount.type !== DiscountType.DiscountCode ||
    item.itemType !== CartItemTypes.Ticket ||
    (discount.scope !== DiscountCodeScope.Tickets && discount.scope !== DiscountCodeScope.Both)
  ) {
    return item.totalPrice;
  }

  const reduction = computeTicketLineReductions(discount, cartItems).get(lineKey(item)) ?? 0;
  if (reduction <= 0) {
    return item.totalPrice;
  }

  return roundToCents(item.totalPrice - reduction);
}

export function getDiscountCodeTotalReduction(
  discount: ICartDiscount | null | undefined,
  cartItems: ICartItem[] = []
): number {
  if (!discount || discount.type !== DiscountType.DiscountCode) {
    return 0;
  }

  let total = 0;

  if (discount.scope === DiscountCodeScope.Kiosk || discount.scope === DiscountCodeScope.Both) {
    total += sumKioskConsumed(cartItems);
  }

  if (discount.scope === DiscountCodeScope.Tickets || discount.scope === DiscountCodeScope.Both) {
    for (const reduction of computeTicketLineReductions(discount, cartItems).values()) {
      total += reduction;
    }
  }

  return roundToCents(total);
}
